import { ReactNode, memo } from "react";
import { GameImage, GamePrice, GamePlatforms } from "@/components/shared";

interface ListCardProps<T extends { appid: number }> {
  item: T;
  onCardClick: (appid: number) => void;
  renderImage: (item: T) => {
    src: string | null;
    alt: string;
    width: number;
    height: number;
  };
  renderTitle: (item: T) => string;
  renderPrice: (item: T) => {
    isFree: boolean | null;
    priceFinal: number | null;
    priceInitial?: number | null;
    priceDiscount?: number | null;
  };
  renderPlatforms?: (item: T) => {
    supportsWindows: boolean | null;
    supportsMac: boolean | null;
    supportsLinux: boolean | null;
  };
  /** Optional badge slot (e.g., rank) */
  renderBadge?: (item: T) => ReactNode;
  /** Optional image overlay (e.g., followers count on image) */
  renderImageOverlay?: (item: T) => ReactNode;
  /** Optional content below image (e.g., followers count) */
  renderImageFooter?: (item: T) => ReactNode;
  /** Optional metadata slot (e.g., ID, date) */
  renderMetadata?: (item: T) => ReactNode;
  /** Optional footer slot */
  renderFooter?: (item: T) => ReactNode;
  className?: string;
}

/**
 * Universal list card component with customizable slots
 * Used for mobile views of game lists, top sellers, etc.
 */
function ListCardInner<T extends { appid: number }>({
  item,
  onCardClick,
  renderImage,
  renderTitle,
  renderPrice,
  renderPlatforms,
  renderBadge,
  renderImageOverlay,
  renderImageFooter,
  renderMetadata,
  renderFooter,
  className = "",
}: ListCardProps<T>) {
  const imageProps = renderImage(item);
  const priceProps = renderPrice(item);
  const platformProps = renderPlatforms?.(item);

  return (
    <button
      type="button"
      onClick={() => onCardClick(item.appid)}
      className={`w-full text-left border-b border-[#2a475e] last:border-b-0 p-4 hover:bg-[#1f2e3f] transition-colors cursor-pointer ${className}`}
    >
      <div className="flex gap-3">
        {/* Optional Badge (e.g., Rank) */}
        {renderBadge && renderBadge(item)}

        {/* Image Container */}
        <div className="flex-shrink-0 flex flex-col">
          <div
            className={`relative`}
            style={{
              width: `${imageProps.width}px`,
              height: `${imageProps.height}px`,
            }}
          >
            <GameImage
              src={imageProps.src}
              alt={imageProps.alt}
              width={imageProps.width}
              height={imageProps.height}
            />
            {renderImageOverlay && (
              <div className="absolute inset-0 flex items-center justify-center">
                {renderImageOverlay(item)}
              </div>
            )}
          </div>
          {renderImageFooter && (
            <div className="mt-1.5" style={{ width: `${imageProps.width}px` }}>
              {renderImageFooter(item)}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          {/* Title — h2 because the page has only h1 above; an h3 here
              skipped a level and failed lighthouse heading-order. */}
          <h2 className="text-white font-medium text-sm mb-1.5 truncate">
            {renderTitle(item)}
          </h2>

          {/* Metadata */}
          {renderMetadata && (
            <div className="text-xs text-[#c7d5e0] mb-1">
              {renderMetadata(item)}
            </div>
          )}

          {/* Price & Platforms */}
          <div className="flex items-center justify-between gap-2">
            {platformProps != null ? (
              <GamePlatforms
                supportsWindows={platformProps.supportsWindows}
                supportsMac={platformProps.supportsMac}
                supportsLinux={platformProps.supportsLinux}
              />
            ) : null}

            <GamePrice
              isFree={priceProps.isFree}
              priceFinal={priceProps.priceFinal}
              priceInitial={priceProps.priceInitial}
              priceDiscount={priceProps.priceDiscount}
              className={priceProps.isFree ? "font-semibold" : ""}
            />
          </div>

          {/* Optional Footer */}
          {renderFooter && renderFooter(item)}
        </div>
      </div>
    </button>
  );
}

export const ListCard = memo(ListCardInner) as typeof ListCardInner;
